/**
 * Design tokens for the new (Shopify admin style) page design.
 *
 * Pages built with the NewDesign components should only take colours, spacing and type from here so
 * the look stays consistent as more pages move across.
 */
import { appFontFamily, brandColors, colors, themeColors } from "../EOSThemeProvider"

export const nd = {
  /** Same font as the rest of the app */
  font: appFontFamily,

  /**
   * Taken from the brand palette (EOSThemeProvider) where one fits. Neutral greys and the darker
   * text shades in each tone are kept so text stays readable on the light backgrounds.
   */
  color: {
    pageBackground: "#f1f1f1",
    surface: brandColors.white,
    surfaceSubdued: brandColors.lightGrey,
    surfaceHover: brandColors.lightGrey,
    border: "#e3e3e3",
    borderStrong: "#c9c9c9",
    /** Text inputs, selects and other form controls */
    inputBorder: "#b5b5b5",
    inputBorderHover: "#8a8a8a",
    /** Selected tab, pressed buttons */
    surfaceSelected: "#e3e3e3",
    /** Dims the page behind a modal */
    overlay: "rgba(0, 0, 0, 0.5)",
    text: brandColors.night,
    textSubdued: brandColors.lightNight,
    textDisabled: brandColors.lighterNight,
    link: brandColors.emerald,
    focus: brandColors.mint,
    /** Primary buttons, the preview bar and other dark UI, as the app header */
    primary: brandColors.night,
    primaryHover: brandColors.darkNight,
    /** Icons on primary buttons, matching the classic toolbar's primary actions */
    primaryIcon: brandColors.mintPop,
    /** Checkboxes and other selected controls */
    accent: brandColors.emerald,
    critical: brandColors.red,
  },

  tone: {
    neutral: {
      background: brandColors.greyLightBackground,
      text: brandColors.lightNight,
      dot: brandColors.lighterNight,
    },
    success: {
      background: brandColors.greenLightBackground,
      text: "#035e55",
      dot: brandColors.emerald,
    },
    info: {
      background: brandColors.blueLightBackground,
      text: colors.usersBlue,
      dot: colors.usersBlue,
    },
    attention: {
      background: brandColors.orangeLightBackground,
      text: "#5e4200",
      dot: brandColors.orange,
    },
    warning: { background: themeColors.lightOrange, text: "#5e4200", dot: themeColors.orange },
    critical: {
      background: brandColors.redLightBackground,
      text: "#8e0b21",
      dot: brandColors.blaze,
    },
  },

  radius: { small: 6, medium: 8, large: 12, full: 9999 },

  shadow: {
    card: "0 1px 0 0 rgba(26, 26, 26, 0.07), inset 0 0 0 1px rgba(0, 0, 0, 0.08)",
    button:
      "0 1px 0 0 rgba(0, 0, 0, 0.05), inset 0 -1px 0 0 #b5b5b5, inset 0 0 0 1px rgba(0, 0, 0, 0.1)",
    buttonPrimary:
      "0 1px 0 0 rgba(0, 0, 0, 0.8), inset 0 1px 0 0 rgba(255, 255, 255, 0.24), inset 0 -1px 0 0 #000",
    popover: "0 4px 16px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.06)",
  },

  space: { xs: 4, sm: 8, md: 12, lg: 16, xl: 20, xxl: 32 },

  fontSize: { small: 12, body: 14, heading: 15, title: 22 },
  fontWeight: { regular: 450, medium: 550, semibold: 650, bold: 700 },

  /**
   * Aside column width, the page width the two columns stack below, and the page width below
   * which the mobile layout kicks in (collapsed header actions, stacked rows). Widths are of the
   * page itself (measured by Page), not the browser window, so the app sidebar is accounted for.
   */
  layout: { asideWidth: 320, stackBelow: 900, mobileBelow: 600 },
} as const

export type INdTone = keyof typeof nd.tone
